import { oc } from "@orpc/contract";
import { z } from "zod";
import { AGENT_PAGE_ICON_KEYS, AGENT_PAGE_NAV_GROUPS } from "@erp/shared-types";
import { AgentRunStatusSchema } from "./agent-definitions.contract";
import {
  PageSectionIdWriteSchema,
  SidebarSortOrderSchema,
} from "./pageSections.contract";

/**
 * Org-configurable "Agent Pages" — one background agent mounted into the
 * sidebar as its own page, with per-page user-group audiences on top of RBAC.
 * The agent-shaped sibling of `taskPages`.
 */

// --- Response schema ---

export const AgentPageResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  // Lenient on output (plain string, not the icon-key enum) so removing a key
  // from the fixed set can never brick reads; the frontend renders a default
  // icon for unknown keys. Writes validate strictly.
  icon: z.string().nullable(),
  agentDefinitionId: z.uuid(),
  // Denormalized from the page's agent definition for sidebar/page rendering.
  agentDefinitionName: z.string(),
  // False when the definition was archived or deactivated after the page was
  // created — the page still renders, but cannot launch runs.
  agentDefinitionIsActive: z.boolean(),
  // Lenient on output for the same reason as `icon`: a renamed sidebar group
  // leaves stale labels behind; the frontend folds unknown labels into the
  // generic "Pages" section. Writes validate against AGENT_PAGE_NAV_GROUPS.
  navGroup: z.string().nullable(),
  // Org-created sidebar section (pageSections.*) the page attaches to. When
  // set it wins over `navGroup`; the frontend folds an unknown id (archived
  // section) into the generic "Pages" section like an unmatched navGroup.
  pageSectionId: z.uuid().nullable(),
  sortOrder: z.number().int(),
  // User groups granted visibility. Empty = visible to every reader.
  audienceUserGroupIds: z.array(z.uuid()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// --- Input schemas ---

const AgentPageWriteShape = {
  name: z.string().trim().min(1).max(255),
  description: z.string().max(10000).nullable().optional(),
  icon: z.enum(AGENT_PAGE_ICON_KEYS).nullable().optional(),
  agentDefinitionId: z.uuid(),
  // Must be one of the shared sidebar group labels; null = generic "Pages"
  // section (shared with task pages).
  navGroup: z.enum(AGENT_PAGE_NAV_GROUPS).nullable().optional(),
  pageSectionId: PageSectionIdWriteSchema,
  sortOrder: SidebarSortOrderSchema.optional(),
  audienceUserGroupIds: z.array(z.uuid()).max(100).optional(),
} as const;

export const CreateAgentPageInputSchema = z.object(AgentPageWriteShape);

// All fields optional on update; `audienceUserGroupIds` (when present)
// REPLACES the audience set atomically.
export const UpdateAgentPageInputSchema = z.object({
  id: z.uuid(),
  ...AgentPageWriteShape,
  name: AgentPageWriteShape.name.optional(),
  agentDefinitionId: z.uuid().optional(),
});

export const ListAgentPagesInputSchema = z.object({
  // 'visible' (default): pages the CURRENT user may see (RBAC read +
  // audience filter) — powers the sidebar. 'manage': every page in the org,
  // regardless of audience — powers the admin card; requires any of Create/
  // Update/Delete on BackgroundAgent.
  view: z.enum(["visible", "manage"]).optional(),
});

const AgentPageIdInputSchema = z.object({ id: z.uuid() });

const ArchiveAgentPageOutputSchema = z.object({ success: z.boolean() });

// Sidebar status for one agent page: the agent's most recent top-level run.
// `latestRunStatus` is null when the agent has never run.
export const AgentPageStatusSchema = z.object({
  latestRunId: z.uuid().nullable(),
  latestRunStatus: AgentRunStatusSchema.nullable(),
  latestRunStartedAt: z.coerce.date().nullable(),
});

// Keyed by agent page id, for the CURRENT user's visible pages only.
export const AgentPageStatusesOutputSchema = z.object({
  statuses: z.record(z.string(), AgentPageStatusSchema),
});

// --- Contracts ---

export const agentPagesContracts = {
  list: oc
    .input(ListAgentPagesInputSchema)
    .output(z.array(AgentPageResponseSchema))
    .route({
      method: "GET",
      summary: "List agent pages",
      description:
        "Lists sidebar agent pages: by default those the current user may see; view 'manage' returns every page in the org for admins. Each page names its background agent.",
    }),

  get: oc.input(AgentPageIdInputSchema).output(AgentPageResponseSchema).route({
    method: "GET",
    summary: "Get an agent page",
    description:
      "Returns one sidebar agent page by id, with its background agent, sidebar section, sort order and audience user groups.",
  }),

  statuses: oc.output(AgentPageStatusesOutputSchema).route({
    method: "GET",
    summary: "Get agent page run statuses",
    description:
      "Returns, for each agent page the current user can see, the agent's most recent run: run id, status and start time.",
  }),

  create: oc
    .input(CreateAgentPageInputSchema)
    .output(AgentPageResponseSchema)
    .route({
      method: "POST",
      summary: "Create an agent page",
      description:
        "Mounts a background agent in the sidebar as its own page, with name, icon, sidebar section, sort order and optional audience user groups.",
    }),

  update: oc
    .input(UpdateAgentPageInputSchema)
    .output(AgentPageResponseSchema)
    .route({
      method: "PUT",
      summary: "Update an agent page",
      description:
        "Changes an agent page's name, description, icon, agent, sidebar section, order or audience; a provided audience list replaces the existing one.",
    }),

  archive: oc
    .input(AgentPageIdInputSchema)
    .output(ArchiveAgentPageOutputSchema)
    .route({
      method: "DELETE",
      summary: "Archive an agent page",
      description:
        "Removes an agent page from the sidebar for everyone. The background agent itself is not changed.",
    }),
} as const;

// --- Type exports ---

export type AgentPageResponse = z.infer<typeof AgentPageResponseSchema>;
export type AgentPageStatus = z.infer<typeof AgentPageStatusSchema>;
export type AgentPageStatusesOutput = z.infer<
  typeof AgentPageStatusesOutputSchema
>;
export type CreateAgentPageInput = z.infer<typeof CreateAgentPageInputSchema>;
export type UpdateAgentPageInput = z.infer<typeof UpdateAgentPageInputSchema>;
export type ListAgentPagesInput = z.infer<typeof ListAgentPagesInputSchema>;
